import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthTokenDecoded } from '../../models/auth.model';
import { FeatureSet } from '../../models/feature.model';

interface PermissionRequest {
  user?: string;
  entity?: string;
  resource: string;
  action: string;
}

interface PermissionResponse {
  allowed: boolean;
}

interface UserPermissionsResponse {
  permissions: string[];
  roles: string[];
  actions: string[];
  entities: string[];
  subjects: string[];
}

// New policy format from API
interface CasbinPolicy {
  pType: string; // "p" for permission rule, "g" for role/group
  p_type?: string;
  v0: string; // typically user
  v1: string; // typically resource/role
  v2?: string | null; // typically action
  v3?: string | null;
  v4?: string | null;
  v5?: string | null;
  PType?: string;
  V0?: string;
  V1?: string;
  V2?: string | null;
  V3?: string | null;
  V4?: string | null;
  V5?: string | null;
}

interface CasbinPolicyResponse {
  success: boolean;
  data: CasbinPolicy[];
  errors: string[];
  messages: string[];
  status_code: number;
  has_data: boolean;
}

interface CasbinPermissionsProxyResponse {
  policies: CasbinPolicy[];
  businessEntityCodes: string[];
  features: string[];
  permissions: string[];
  groups: string[];
}

interface UserPermissionSet {
  businessEntityCodes: string[];
  features: string[];
  permissions: string[];
  groups: string[];
}

@Injectable({
  providedIn: 'root',
})
export class CasbinPermissionsService {
  private http = inject(HttpClient);
  private initialized$ = new BehaviorSubject<boolean>(false);
  private readonly adminPermissionSet: UserPermissionSet = {
    permissions: ['*:*'],
    features: ['*'],
    businessEntityCodes: [],
    groups: ['admin'],
  };

  permissionSet = signal<UserPermissionSet>({
    permissions: [],
    features: [],
    businessEntityCodes: [],
    groups: [],
  });

  private readonly emptyPermissionSet: UserPermissionSet = {
    permissions: [],
    features: [],
    businessEntityCodes: [],
    groups: [],
  };

  permissions = computed(() => this.permissionSet().permissions);
  businessEntityCodes = computed(
    () => this.permissionSet().businessEntityCodes,
  );
  hasFullAccess = computed(() => this.permissions().includes('*:*'));

  permissionTo = (permission: string) => {
    console.log('permission', this.permissions());
    return computed(() => {
      return this.permissions().includes(permission) || this.hasFullAccess();
    });
  };

  userIdToken = signal<string>('');

  clearPermissions(): void {
    this.permissionSet.set(this.emptyPermissionSet);
    this.initialized$.next(false);
  }

  private async loadUserPermissions(accessToken: string): Promise<void> {
    this.userIdToken.set(accessToken);
    const isAdminToken = this.isAdminToken(accessToken);

    try {
      const response = await firstValueFrom(
        this.http.get<
          CasbinPolicy[] | CasbinPolicyResponse | CasbinPermissionsProxyResponse
        >('v1/user/policy'),
      );
      const policies = this.extractPolicies(response);

      if (policies.length > 0) {
        this.permissionSet.set(this.getPermissionSetFromPolicies(policies));
        this.initialized$.next(true);
      } else if (isAdminToken) {
        this.grantAdminFullAccess();
      } else {
        throw new Error('No policies found for the current user');
      }
    } catch (error) {
      if (isAdminToken) {
        console.warn(
          'Policy loading failed for an admin token. Granting full access from JWT admin claim.',
          error,
        );
        this.grantAdminFullAccess();
        return;
      }

      console.error('Failed to load user permissions:', error);
      this.clearPermissions();
      throw error;
    }
  }

  hasPermission(
    featureSet?: FeatureSet,
    feature?: string,
    entity?: string,
  ): boolean {
    if (!featureSet || !feature) return true;
    if (!this.initialized$.value) return false;
    if (!this.hasBusinessEntityAccess(entity)) return false;

    // Build permission key (e.g., "package:manage", "dock:view")
    const key = `${featureSet}:${feature}`;

    return (
      this.permissionSet().permissions.includes(key) ||
      this.hasFullAccess() ||
      false
    );
  }

  canView(featureSet?: FeatureSet, entity?: string): boolean {
    return (
      featureSet === 'public' || this.hasPermission(featureSet, 'view', entity)
    );
  }

  hasBusinessEntityAccess(entity?: string | null): boolean {
    if (!this.initialized$.value) return false;

    const normalizedEntity = entity?.trim();
    if (!normalizedEntity) return true;
    if (this.hasFullAccess()) return true;

    const allowedEntities = this.permissionSet().businessEntityCodes;
    if (allowedEntities.length === 0) return true;

    return allowedEntities.includes(normalizedEntity);
  }

  async reloadPolicies(accessToken: string): Promise<void> {
    this.clearPermissions();
    try {
      await this.loadUserPermissions(accessToken);
      console.log('Policies reloaded successfully');
    } catch (error) {
      console.error('Failed to reload policies:', error);
      throw error;
    }
  }

  // For individual permission checks (uses backend API)
  async checkPermissionAsync(
    featureSet: FeatureSet,
    feature: string,
    entity?: string,
  ): Promise<boolean> {
    try {
      const request: PermissionRequest = {
        entity: entity || 'null',
        resource: featureSet,
        action: feature,
      };

      const response = await firstValueFrom(
        this.http.post<PermissionResponse>('casbin/check', request),
      );

      return response.allowed;
    } catch (error) {
      console.error('Async permission check failed:', error);
      return false;
    }
  }

  getPermissionSetFromResponse(
    response: UserPermissionsResponse,
  ): UserPermissionSet {
    return {
      businessEntityCodes: response.roles,
      features: response.entities,
      permissions: response.permissions,
      groups: [],
    };
  }

  /**
   * Parse Casbin policy response to extract user permissions
   * pType 'p' = permission rule (v0: user, v1: resource, v2: action)
   * pType 'g' = role/group assignment (v0: user, v1: role/group)
   */
  private getPermissionSetFromPolicies(
    policies: CasbinPolicy[],
  ): UserPermissionSet {
    const permissions = new Set<string>();
    const groups = new Set<string>();

    for (const policy of policies) {
      const normalizedPolicy = this.normalizePolicy(policy);

      if (
        normalizedPolicy.pType === 'p' &&
        normalizedPolicy.v1 &&
        normalizedPolicy.v2
      ) {
        // Permission rule: user, resource, action
        // v0 = user, v1 = resource (e.g., "package", "dock"), v2 = action (e.g., "manage", "view")
        const permission = `${normalizedPolicy.v1}:${normalizedPolicy.v2}`;
        permissions.add(permission);
      } else if (normalizedPolicy.pType === 'g' && normalizedPolicy.v1) {
        // Group assignment: user is member of group
        // v0 = user, v1 = group (e.g., "NDCC", "DFW")
        groups.add(normalizedPolicy.v1);
      }
    }

    if (groups.has('admin')) {
      permissions.add('*:*');
    }

    return {
      businessEntityCodes: Array.from(groups),
      features: Array.from(permissions).map((p) => p.split(':')[0]),
      permissions: Array.from(permissions),
      groups: Array.from(groups),
    };
  }

  private grantAdminFullAccess(): void {
    this.permissionSet.set(this.adminPermissionSet);
    this.initialized$.next(true);
  }

  private isAdminToken(accessToken: string): boolean {
    try {
      const decodedToken = jwtDecode<AuthTokenDecoded>(accessToken);
      const adminClaim = decodedToken.admin;

      if (typeof adminClaim !== 'string') {
        return false;
      }

      return adminClaim.toLowerCase() === 'true';
    } catch (error) {
      console.warn('Failed to decode access token for Casbin fallback.', error);
      return false;
    }
  }

  private normalizePolicy(policy: CasbinPolicy): CasbinPolicy {
    return {
      pType: policy.pType ?? policy.p_type ?? policy.PType ?? '',
      v0: policy.v0 ?? policy.V0 ?? '',
      v1: policy.v1 ?? policy.V1 ?? '',
      v2: policy.v2 ?? policy.V2 ?? null,
      v3: policy.v3 ?? policy.V3 ?? null,
      v4: policy.v4 ?? policy.V4 ?? null,
      v5: policy.v5 ?? policy.V5 ?? null,
    };
  }

  private extractPolicies(
    response:
      | CasbinPolicy[]
      | CasbinPolicyResponse
      | CasbinPermissionsProxyResponse,
  ): CasbinPolicy[] {
    if (Array.isArray(response)) {
      return response;
    }

    if ('data' in response && Array.isArray(response.data)) {
      return response.data;
    }

    if ('policies' in response && Array.isArray(response.policies)) {
      return response.policies;
    }

    return [];
  }
}
